import { useState } from "react";
import { Link } from "react-router-dom";
import { usePerformanceFilters } from "@/hooks/usePerformanceFilters";
import { useRacePerformance } from "@/hooks/useRacePerformance";
import {
  EDITION_BY_KEY,
  LENSES,
  REPORT_TYPES,
} from "@/utils/performance/constants";
import {
  buildDetailRows,
  computeSummary,
  filterRaces,
} from "@/utils/performance/metrics";
import { periodRangeFor, resolvePeriodRange } from "@/utils/performance/period";
import PerformanceFilterBar from "@/components/performance/PerformanceFilterBar";
import FullWidthToggle from "@/components/performance/FullWidthToggle";
import RacePerformanceTable from "@/components/performance/RacePerformanceTable";

const labelFor = (list, key, fallback) =>
  list.find((x) => x.key === key)?.label || fallback;

function Chip({ children }) {
  return (
    <span className="bg-white border border-emerald-200 text-emerald-800 rounded-full px-2.5 py-0.5 text-xs font-medium">
      {children}
    </span>
  );
}

function LegendItem({ swatch, children }) {
  return (
    <span className="flex items-center gap-1">
      {swatch}
      {children}
    </span>
  );
}

/**
 * Lexi Performance — Detail (race by race).
 * Arrived at from the summary (filters carried in the URL) and refinable here.
 * Built from docs/mockups/lexi-performance-detail.html against mock data.
 */
// The persisted store starts here; used to clamp the period stepper.
const STORE_FLOOR = "2025-12-01";

export default function PerformanceDetail() {
  const { filters, setFilter } = usePerformanceFilters();
  const [isFullWidth, setIsFullWidth] = useState(false);

  // Detail renders the full field, so it needs the runners (mode: full) — but
  // only for the period's slice, computed up front from the anchor (default:
  // today) so a drill-down doesn't pull the whole window.
  const todayIso = new Date().toISOString().slice(0, 10);
  const fetchRange = periodRangeFor(
    filters.period,
    filters.anchor || todayIso,
    {
      from: filters.from,
      to: filters.to,
    },
  );
  const { data: races = [], isLoading } = useRacePerformance(
    fetchRange.from && fetchRange.to
      ? {
          mode: "full",
          from: fetchRange.from,
          to: fetchRange.to,
          track: filters.track,
        }
      : { mode: "full", track: filters.track },
  );

  // Full track list for the dropdown — a cheap lean fetch that shares the
  // summary page's cache (the table fetch above is track-filtered, so it can't
  // populate the dropdown on its own).
  const { data: recent = [] } = useRacePerformance({ mode: "summary" });
  const tracks = [...new Set(recent.map((r) => r.track))].sort();

  const range = resolvePeriodRange(filters.period, races, {
    anchor: filters.anchor,
    from: filters.from,
    to: filters.to,
  });
  // Clamp the period stepper across the whole store, not just the loaded slice.
  const dataMin = STORE_FLOOR;
  const dataMax = todayIso;
  const filtered = filterRaces(races, {
    ...filters,
    from: range.from,
    to: range.to,
  });
  const rows = buildDetailRows(filtered, filters);
  const summary = computeSummary(filtered, filters);

  const editionLabel =
    filters.edition === "all"
      ? "All editions"
      : EDITION_BY_KEY[filters.edition]?.label || filters.edition;

  return (
    <div className="min-h-screen bg-slate-100 text-slate-800">
      <div
        className={`${isFullWidth ? "w-full" : "max-w-[1240px]"} mx-auto p-6`}
      >
        <div className="flex items-center justify-between mb-2">
          <div>
            <Link
              to={`/performance?${new URLSearchParams(
                Object.entries(filters).filter(
                  ([, v]) => v && v !== "all" && v !== "",
                ),
              ).toString()}`}
              className="text-sm text-emerald-700 font-semibold"
            >
              ← Back to summary
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 mt-1">
              Lexi Performance{" "}
              <span className="text-slate-400 font-normal">
                / Detail — race by race
              </span>
            </h1>
          </div>
          <FullWidthToggle
            isFullWidth={isFullWidth}
            onToggle={() => setIsFullWidth((prev) => !prev)}
          />
        </div>

        {/* Carried-over context */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-4 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-emerald-800 font-semibold">Showing:</span>
          <Chip>{labelFor(REPORT_TYPES, filters.type, "Multi")}</Chip>
          <Chip>
            {filters.period[0].toUpperCase() + filters.period.slice(1)} ·{" "}
            {range.label}
          </Chip>
          {filters.track !== "all" && (
            <button onClick={() => setFilter({ track: "all" })}>
              <Chip>Track: {filters.track} ✕</Chip>
            </button>
          )}
          <Chip>Edition: {editionLabel}</Chip>
          <Chip>Lens: {labelFor(LENSES, filters.lens, "Auto")}</Chip>
          <span className="ml-auto text-emerald-700 text-xs">
            {summary.headline.legs} races · {summary.headline.cashed} cashed ·{" "}
            <b>{summary.headline.rate.toFixed(1)}%</b>
          </span>
        </div>

        <PerformanceFilterBar
          filters={filters}
          setFilter={setFilter}
          tracks={tracks}
          rangeLabel={range.label}
          dataMin={dataMin}
          dataMax={dataMax}
          highlightTrack
        />

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 mb-2">
          <LegendItem
            swatch={
              <span className="inline-block w-3 h-3 rounded bg-emerald-500" />
            }
          >
            Lexi tip — placed in range (hit)
          </LegendItem>
          <LegendItem
            swatch={
              <span className="inline-block w-3 h-3 rounded bg-slate-300" />
            }
          >
            Lexi tip — missed
          </LegendItem>
          <LegendItem
            swatch={
              <span className="inline-block w-3 h-3 rounded bg-emerald-50 border border-emerald-200" />
            }
          >
            positions inside range
          </LegendItem>
          <LegendItem
            swatch={<span className="inline-block w-3 h-1.5 bg-emerald-500" />}
          >
            leg cashed (row accent)
          </LegendItem>
          <span className="text-slate-400">
            badges: <b>E</b> early · <b>RD</b> race-day · <b>LT</b> late
          </span>
        </div>

        {isLoading ? (
          <div className="text-center text-slate-400 py-20">Loading…</div>
        ) : (
          <RacePerformanceTable rows={rows} />
        )}

        <p className="text-center text-xs text-slate-400">
          Live data — arrived from the summary; filters carried over &amp;
          refinable. See <code>docs/LEXI-PERFORMANCE-REPORTS-SPEC.md</code>.
        </p>
      </div>
    </div>
  );
}
